import React, { useState } from "react";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../../firebase/config";
import OnboardingChecklist from "./OnboardingChecklist";
import QuarterlyCheckInForm from "./QuarterlyCheckInForm";
import ActivityHistoryList from "./ActivityHistoryList";
import { makeEmptyQuarterlyForm } from "./constants";

// Activity Log ("Contact Log") modal: onboarding checklist, note entry
// (general / contact / quarterly check-in), and the activity history.
const ActivityLogModal = ({ submission, onClose, onEditApplication, currentAdminEmail, showToast, onSubmissionChange }) => {
    const [newNote, setNewNote] = useState("");
    const [noteCategory, setNoteCategory] = useState("general");
    const [addingNote, setAddingNote] = useState(false);
    const [revertingEntry, setRevertingEntry] = useState(null);
    const [quarterlyForm, setQuarterlyForm] = useState(makeEmptyQuarterlyForm());

    const addQuarterlyCheckin = async () => {
        if (!submission) return;
        if (!quarterlyForm.overallRating) {
            showToast("Please select an overall partnership rating.", "error");
            return;
        }

        setAddingNote(true);
        const recordId = submission.id;
        try {
            const docRef = doc(db, "medical_providers", recordId);
            const noteEntry = {
                id: Date.now().toString(),
                type: "note",
                category: "quarterly",
                timestamp: new Date(),
                adminEmail: currentAdminEmail,
                quarterlyData: { ...quarterlyForm },
                content: `${quarterlyForm.quarter} ${new Date().getFullYear()} Check-In — Rating: ${quarterlyForm.overallRating}`,
            };

            await updateDoc(docRef, {
                activityLog: arrayUnion(noteEntry),
            });

            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    activityLog: [...(prev.activityLog || []), noteEntry],
                };
            });
            setQuarterlyForm(makeEmptyQuarterlyForm());
            showToast("Quarterly check-in saved");
        } catch (error) {
            console.error("Error saving quarterly check-in:", error);
            showToast("Failed to save. Please try again.", "error");
        } finally {
            setAddingNote(false);
        }
    };

    const addNote = async () => {
        if (noteCategory === "quarterly") {
            return addQuarterlyCheckin();
        }
        if (!newNote.trim() || !submission) return;

        setAddingNote(true);
        const recordId = submission.id;
        try {
            const docRef = doc(db, "medical_providers", recordId);
            const noteEntry = {
                id: Date.now().toString(),
                type: "note",
                category: noteCategory,
                timestamp: new Date(),
                adminEmail: currentAdminEmail,
                content: newNote.trim(),
            };

            await updateDoc(docRef, {
                activityLog: arrayUnion(noteEntry),
            });

            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    activityLog: [...(prev.activityLog || []), noteEntry],
                };
            });
            setNewNote("");
            showToast(noteCategory === "contact" ? "Contact log added" : "Note added");
        } catch (error) {
            console.error("Error adding note:", error);
            showToast("Failed to add note. Please try again.", "error");
        } finally {
            setAddingNote(false);
        }
    };

    const revertEdit = async (entry) => {
        if (entry.type !== "edit") return;

        const confirmed = window.confirm(
            `Revert "${entry.fieldLabel}" back to "${entry.oldValue}"?`
        );
        if (!confirmed) return;

        setRevertingEntry(entry.id);
        const recordId = submission.id;
        try {
            const docRef = doc(db, "medical_providers", recordId);

            // Create a revert activity entry
            const revertEntry = {
                id: Date.now().toString(),
                type: "revert",
                timestamp: new Date(),
                adminEmail: currentAdminEmail,
                field: entry.field,
                fieldLabel: entry.fieldLabel,
                revertedFrom: entry.newValue,
                revertedTo: entry.oldValue,
                originalEditId: entry.id,
            };

            await updateDoc(docRef, {
                [entry.field]: entry.oldValue === "(empty)" ? "" : entry.oldValue,
                lastModified: new Date(),
                activityLog: arrayUnion(revertEntry),
            });

            // Update modal data
            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    [entry.field]: entry.oldValue === "(empty)" ? "" : entry.oldValue,
                    activityLog: [...(prev.activityLog || []), revertEntry],
                };
            });

            showToast(`Reverted "${entry.fieldLabel}"`);
        } catch (error) {
            console.error("Error reverting edit:", error);
            showToast("Failed to revert. Please try again.", "error");
        } finally {
            setRevertingEntry(null);
        }
    };

    return (
        <div
            className="modal-overlay"
            onClick={onClose}>
            <div
                className="modal-content activity-modal"
                onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>📋 Activity Log</h2>
                    <button
                        className="close-btn"
                        onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    <div className="contractor-info-banner">
                        <div className="contractor-info-text">
                            <strong>{submission.name}</strong>
                            <span>{submission.email}</span>
                        </div>
                        <button
                            className="edit-application-btn"
                            onClick={() => onEditApplication(submission)}
                        >
                            ✏️ Edit Application
                        </button>
                    </div>

                    {/* Internal Onboarding Checklist */}
                    <OnboardingChecklist
                        submission={submission}
                        currentAdminEmail={currentAdminEmail}
                        showToast={showToast}
                        onSubmissionChange={onSubmissionChange}
                    />

                    {/* Add Note Section */}
                    <div className="add-note-section">
                        <div className="note-category-selector">
                            <button
                                className={`category-tab ${noteCategory === 'general' ? 'active' : ''}`}
                                onClick={() => setNoteCategory('general')}
                            >💬 General Note</button>
                            <button
                                className={`category-tab ${noteCategory === 'contact' ? 'active' : ''}`}
                                onClick={() => setNoteCategory('contact')}
                            >📞 Contact Log</button>
                            <button
                                className={`category-tab ${noteCategory === 'quarterly' ? 'active' : ''}`}
                                onClick={() => setNoteCategory('quarterly')}
                            >📊 Quarterly Check-In</button>
                        </div>

                        {noteCategory !== 'quarterly' ? (
                            <>
                                <textarea
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    placeholder={noteCategory === 'contact'
                                        ? "Log contact details — who was contacted, method, outcome..."
                                        : "Add a general note about this application..."}
                                    rows="3"
                                />
                                <button
                                    className="add-note-btn"
                                    onClick={addNote}
                                    disabled={!newNote.trim() || addingNote}
                                >
                                    {addingNote ? "Adding..." : noteCategory === 'contact' ? "📞 Log Contact" : "💬 Add Note"}
                                </button>
                            </>
                        ) : (
                            <QuarterlyCheckInForm
                                quarterlyForm={quarterlyForm}
                                setQuarterlyForm={setQuarterlyForm}
                                onSubmit={addNote}
                                saving={addingNote}
                            />
                        )}
                    </div>

                    {/* Activity List */}
                    <ActivityHistoryList
                        activityLog={submission.activityLog}
                        revertingEntry={revertingEntry}
                        onRevert={revertEdit}
                    />
                </div>
            </div>
        </div>
    );
};

export default ActivityLogModal;
